import React, { useCallback, useRef, useState } from "react";
import { UseFormRegister, UseFormSetValue, FieldValues, Path } from "react-hook-form";
import InputValidationTooltip from "../../common/InputValidationTooltip";
import WhereAAAMemberNumberModal from "components/modal/where-aaa-member-number/WhereAAAMemberNumberModal";
import { nonDigitGlobalRegex, VALIDATION_MESSAGES_CARD } from "constants/validations";

interface AAAMemberFieldProps<T extends FieldValues> {
    register: UseFormRegister<T>;
    setValue: UseFormSetValue<T>;
    name: Path<T>;
    error?: string;
    value: string;
    id?: string;
}

/**
 * Formats AAA member number as: 00 123 4567890123 4
 * Pattern: 2 digits, space, 3 digits, space, 10 digits, space, 1 digit
 */
const formatAAAMemberNumber = (digitsOnly: string): string => {
    const digits = digitsOnly.replace(nonDigitGlobalRegex, "").slice(0, 16);

    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
    if (digits.length <= 15) return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
    return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 15)} ${digits.slice(15)}`;
};

/**
 * Reusable form field for AAA member number input with formatting.
 * Accepts only numeric input (16 digits) and displays with pattern: 00 123 4567890123 4
 */
const AAAMemberField = <T extends FieldValues>({ register, setValue, name, error, value, id }: AAAMemberFieldProps<T>) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const original = e.target.value;
            const hasNonDigits = nonDigitGlobalRegex.test(original.replace(/\s/g, ""));

            if (hasNonDigits && original.length > 0) {
                setShowTooltip(true);

                if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);

                tooltipTimeoutRef.current = setTimeout(() => {
                    setShowTooltip(false);
                }, 2000);
            }

            // Extract digits only and update form value
            const digits = original.replace(nonDigitGlobalRegex, "").slice(0, 16);
            setValue(name, digits as any, { shouldDirty: true, shouldTouch: true });
        },
        [name, setValue]
    );

    // Destructure ref from register to merge with inputRef
    const { ref: registerRef, ...registerRest } = register(name, {
        required: VALIDATION_MESSAGES_CARD.FIELD_REQUIRED,
        setValueAs: (v) =>
            String(v ?? "")
                .replace(nonDigitGlobalRegex, "")
                .slice(0, 16),
        onChange: handleChange,
    });

    return (
        <div className="form-control">
            <label className="label h-7" htmlFor={id}>
                <span className="text-base-content">AAA Member Number</span>
            </label>
            <input
                type="text"
                inputMode="numeric"
                placeholder="00 123 4567890123 4"
                value={formatAAAMemberNumber(value || "")}
                className={`input input-bordered bg-base-100 h-9 text-sm ${error ? "input-error" : ""}`}
                maxLength={19}
                id={id}
                {...registerRest}
                ref={(e) => {
                    registerRef(e);
                    inputRef.current = e;
                }}
            />
            <InputValidationTooltip message="Only numbers are allowed" show={showTooltip} targetRef={inputRef} />
            {error && <p className="text-error text-xs mt-1">{error}</p>}
            <WhereAAAMemberNumberModal />
        </div>
    );
};

export default AAAMemberField;
